import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import 'bootstrap/dist/css/bootstrap.min.css';
import './css/Body.css';

import Header from './js/Components/Header';
import Footer from './js/Components/Footer';
import Breadcrumb from './js/Components/Breadcrumb';
import Home from './js/Pages/Home';
import About from './js/Pages/About';
import Contact from './js/Pages/Contact';
import Prices from './js/Pages/Prices';
import PhotoGallery from './js/Pages/Gallery';
import Account from './js/Pages/Account';
import AdminPanel from './js/Pages/Admin/AdminPanel';
import { publicRouteSlugs, SiteSettingsProvider, useSiteSettings } from './js/siteSettings';

type ThemeMode = 'light' | 'dark';

const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  return window.localStorage.getItem('dentalpuente-theme') === 'dark' ? 'dark' : 'light';
};

const ProtectedPublicRoute: React.FC<{ slug: string; children: React.ReactElement }> = ({ slug, children }) => {
  const { isReady, isPageVisible } = useSiteSettings();
  if (!isReady) return null;
  return isPageVisible(slug) ? children : <Navigate to="/" replace />;
};

const SiteMetaSync: React.FC = () => {
  const location = useLocation();
  const { getPageByRoute, settings } = useSiteSettings();

  React.useEffect(() => {
    const page = getPageByRoute(location.pathname);
    const description = page?.meta_description || settings.default_meta_description || '';
    const keywords = page?.meta_keywords || settings.default_meta_keywords || '';

    const upsertMeta = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement('meta');
        element.name = name;
        document.head.appendChild(element);
      }
      element.content = content;
    };

    if (page?.browser_title) document.title = page.browser_title;
    if (description) upsertMeta('description', description);
    if (keywords) upsertMeta('keywords', keywords);
  }, [getPageByRoute, location.pathname, settings.default_meta_description, settings.default_meta_keywords]);

  return null;
};

const AppShell: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/AdminPanel');
  const variant: 'container' | 'admin' = isAdminRoute ? 'admin' : 'container';
  const [isAdminSidebarCollapsed, setIsAdminSidebarCollapsed] = React.useState<boolean>(false);
  const [themeMode, setThemeMode] = React.useState<ThemeMode>(getInitialTheme);

  React.useEffect(() => {
    if (!isAdminRoute) setIsAdminSidebarCollapsed(false);
  }, [isAdminRoute]);

  React.useEffect(() => {
    document.body.dataset.theme = themeMode;
    window.localStorage.setItem('dentalpuente-theme', themeMode);
  }, [themeMode]);

  const toggleThemeMode = () => {
    setThemeMode((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
  };

  return (
    <>
      <SiteMetaSync />
      <div className={`app-shell app-shell-${variant} ${isAdminSidebarCollapsed ? 'admin-sidebar-collapsed' : ''}`}>
        <Header
          variant={variant}
          themeMode={themeMode}
          onToggleThemeMode={toggleThemeMode}
          isAdminSidebarCollapsed={isAdminSidebarCollapsed}
          onToggleAdminSidebar={() => setIsAdminSidebarCollapsed((currentValue) => !currentValue)}
        />
        <main className={`main-content main-content-${variant}`}>
          <Breadcrumb variant={variant} />
          <Routes>
            <Route path="/Account" element={<ProtectedPublicRoute slug={publicRouteSlugs['/Account']}><Account /></ProtectedPublicRoute>} />
            <Route path="/About" element={<ProtectedPublicRoute slug={publicRouteSlugs['/About']}><About /></ProtectedPublicRoute>} />
            <Route path="/Gallery" element={<ProtectedPublicRoute slug={publicRouteSlugs['/Gallery']}><PhotoGallery /></ProtectedPublicRoute>} />
            <Route path="/Contact" element={<ProtectedPublicRoute slug={publicRouteSlugs['/Contact']}><Contact /></ProtectedPublicRoute>} />
            <Route path="/Prices" element={<ProtectedPublicRoute slug={publicRouteSlugs['/Prices']}><Prices /></ProtectedPublicRoute>} />
            <Route path="/AdminPanel/*" element={<AdminPanel />} />
            <Route path="/" element={<Home />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <Footer variant={variant} />
    </>
  );
};

const App: React.FC = () => (
  <Router>
    <SiteSettingsProvider>
      <AppShell />
    </SiteSettingsProvider>
  </Router>
);

export default App;
